import { useEffect, useState } from 'react'
import { useNavigate, Link, useSearchParams, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { setStoredToken, getStoredToken, getChildToken } from '../auth.js'
import { setLanguage, LANGUAGE_LABELS, SUPPORTED } from '../i18n.js'

import MusicConsentSheet from '../components/MusicConsentSheet.jsx'
import { toggleGlobalMusic } from '../hooks/useMusic.js'
import { KofiFloating, KofiButton, KofiModal } from '../components/KofiWidget.jsx'

export default function Login() {
  const { t, i18n } = useTranslation()
  const [currentLang, setCurrentLang] = useState(i18n.language || 'sv')
  function handleLangChange(lang) { setLanguage(lang); setCurrentLang(lang) }
  
  const token = getStoredToken()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [demoLoading, setDemoLoading] = useState(false)
  const [visible, setVisible] = useState(false)
  const [passwordResetEnabled, setPasswordResetEnabled] = useState(false)
  const [kofiModalOpen, setKofiModalOpen] = useState(false)
  const [requiresVerification, setRequiresVerification] = useState(false)
  const [verificationEmail, setVerificationEmail] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [showDeletedNotice, setShowDeletedNotice] = useState(() => searchParams.get('deleted') === '1')

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    api.auth.passwordResetStatus()
      .then((data) => setPasswordResetEnabled(data?.enabled === true))
      .catch(() => setPasswordResetEnabled(false))
  }, [])

  useEffect(() => {
    setShowDeletedNotice(searchParams.get('deleted') === '1')
  }, [searchParams])

  if (getChildToken()) return <Navigate to="/child" replace />
  if (token) return <Navigate to="/dashboard" replace />

  async function handleDemo() {
    setDemoLoading(true)
    setError('')
    try {
      const data = await api.auth.demo()
      setStoredToken(data.token)
      navigate('/dashboard')
    } catch (err) {
      setError(err.message || 'Kunde inte starta demo. Försök igen senare.')
      setDemoLoading(false)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const data = await api.auth.login(email, password)
      if (data.requires_verification) {
        setRequiresVerification(true)
        setVerificationEmail(data.email)
        setError('')
      } else {
        setStoredToken(data.token)
        const joinCode = searchParams.get('join')
        const familyInvite = searchParams.get('familyInvite')
        navigate(familyInvite ? `/family-invite/${familyInvite}` : joinCode ? `/join/${joinCode}` : '/dashboard')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleResendVerification() {
    setResendLoading(true)
    setError('')
    try {
      await api.auth.resendVerification(verificationEmail)
      setError('') // Clear any previous errors
      // Show success message by temporarily updating error state
      const msg = t('login.resendSuccess')
      setError(msg)
      setTimeout(() => setError(''), 3000)
    } catch (err) {
      setError(err.message)
    } finally {
      setResendLoading(false)
    }
  }

  return (
    <div className="relative flex min-h-screen flex-col justify-center bg-gradient-to-b from-indigo-600/90 to-indigo-800/95 px-4 dark:from-[#08101c]/95 dark:via-[#0d1626]/95 dark:to-slate-900/95 md:py-8">
      <div className="max-w-sm mx-auto w-full">
        <div className="text-center mb-8">
          <img src="/icons/logo.png" alt="Grannäventyret" className="mx-auto mb-3 w-48 animate-float-pulse drop-shadow-lg sm:w-64" />
          <p className="mt-1 text-white/70 dark:text-slate-400">{t('login.tagline')}</p>
          <p className="mt-2 text-sm">
            <Link to="/" className="text-white/80 underline decoration-white/40 underline-offset-4 hover:text-white dark:text-slate-300">
              {t('login.learnMore')}
            </Link>
          </p>
        </div>

        <div className={`rounded-2xl bg-white p-6 shadow-xl transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} dark:bg-slate-900 dark:shadow-black/20`}>
           {requiresVerification ? (
            <>
              <div className="text-center mb-6">
                <p className="text-4xl mb-4">📧</p>
                <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mb-2">{t('login.notVerifiedTitle')}</h2>
                <p className="text-sm text-gray-600 dark:text-slate-400">{t('login.notVerifiedDesc')}</p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {t('login.verificationSentTo', { email: verificationEmail })}
                </p>
              </div>
              {error && (
                <p className={`rounded-lg px-3 py-2 text-sm mb-4 ${
                  error.includes('skickat') 
                    ? 'bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-300'
                    : 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300'
                }`}>
                  {error}
                </p>
              )}
               <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendLoading}
                className="w-full rounded-lg bg-sky-600 py-3 font-semibold text-white transition btn-pop shadow-md shadow-sky-500/30 hover:bg-sky-700 disabled:opacity-50 dark:bg-sky-500 dark:text-slate-950 dark:hover:bg-sky-400 mb-3"
              >
                {resendLoading ? t('login.sending') : t('login.resend')}
              </button>
              <p className="text-center text-sm text-gray-500 dark:text-slate-400">
                <button
                  type="button"
                  onClick={() => setRequiresVerification(false)}
                  className="font-medium text-sky-600 hover:underline dark:text-sky-300"
                >
                  {t('login.backToLogin')}
                </button>
              </p>
            </>
           ) : (
            <>
              <h2 className="mb-5 text-xl font-semibold text-gray-800 dark:text-slate-100">{t('login.title')}</h2>
              {showDeletedNotice && (
            <div className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-500/40 bg-amber-50/90 px-3 py-3 text-sm text-amber-900 shadow-[0_12px_30px_rgba(234,179,8,0.15)] dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-100" role="alert">
              <div className="text-base">⚠️</div>
              <div className="flex-1 text-sm leading-relaxed">
                {t('login.deletedNotice')}
              </div>
              <button
                type="button"
                onClick={() => setShowDeletedNotice(false)}
                className="rounded-full p-1 text-xs text-amber-600 hover:bg-amber-100/60 hover:text-amber-900 dark:text-amber-200 dark:hover:bg-amber-400/10"
                aria-label="Stäng meddelande"
              >✕</button>
            </div>
          )}
           <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-slate-300">{t('login.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-200 focus:ring-2 focus:ring-sky-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                placeholder="din@epost.se"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-slate-300">{t('login.password')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-200 focus:ring-2 focus:ring-sky-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                placeholder="••••••••"
              />
            </div>
            {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-300">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-sky-600 py-3 font-semibold text-white transition btn-pop shadow-md shadow-sky-500/30 hover:bg-sky-700 disabled:opacity-50 dark:bg-sky-500 dark:text-slate-950 dark:hover:bg-sky-400"
            >
              {loading ? t('login.submitting') : t('login.submit')}
            </button>
            {passwordResetEnabled && (
              <div className="text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm text-gray-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-sky-400"
                >
                  {t('login.forgotPassword')}
                </Link>
              </div>
            )}
          </form>
          <p className="mt-4 text-center text-sm text-gray-500 dark:text-slate-400">
            {t('login.noAccount')}{' '}
            <Link to="/register" className="font-medium text-sky-600 hover:underline dark:text-sky-300">
              {t('login.createAccount')}
            </Link>
          </p>
               <div className="mt-3 text-center">
                  <button
                    type="button"
                    onClick={handleDemo}
                    disabled={demoLoading}
                    className="text-sm text-gray-400 hover:text-indigo-600 disabled:opacity-50 dark:text-slate-500 dark:hover:text-indigo-400"
                  >
                    {demoLoading ? t('login.starting') : t('login.tryDemo')}
                  </button>
                 {error && demoLoading === false && (
                   <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-300">{error}</p>
                 )}
               </div>
            </>
          )}
        </div>
        <div className="mt-4 flex justify-center gap-2">
          {SUPPORTED.map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => handleLangChange(lang)}
              title={LANGUAGE_LABELS[lang]}
              className={`rounded-full px-3 py-1 text-sm transition ${
                currentLang === lang
                  ? 'bg-white/20 font-semibold text-white'
                  : 'text-white/50 hover:text-white/80'
              }`}
            >
              {lang === 'sv' ? '🇸🇪' : lang === 'no' ? '🇳🇴' : lang === 'da' ? '🇩🇰' : '🇫🇮'}
            </button>
          ))}
        </div>
      </div>
      <KofiFloating />
      <div className="fixed bottom-4 left-4 md:hidden z-50">
        <KofiButton onClick={() => setKofiModalOpen(true)} />
      </div>
      <KofiModal open={kofiModalOpen} onClose={() => setKofiModalOpen(false)} />
      <MusicConsentSheet onChoice={(wantsMusic) => {
        // Default state is muted. toggleGlobalMusic() handles both localStorage and audio.
        // "Ja" → unmute (toggle from muted=true to muted=false)
        // "Nej" → stay muted, but write preference so sheet won't show again
        if (wantsMusic) {
          toggleGlobalMusic() // unmutes and starts playing
        } else {
          try { localStorage.setItem('nq_music_muted', 'true') } catch {}
        }
      }} />
    </div>
  )
}
